/*
 * Barra de compra fixa (sticky-add-to-cart.liquid).
 *
 * Mostra/esconde a barra conforme o botão de compra REAL entra e sai da tela,
 * com histerese para não piscar, e leva o clique de volta ao campo de variantes
 * em vez de adicionar ao carrinho.
 *
 * Sem polling: leitura de geometria no máximo uma vez por frame (rAF) e
 * MutationObserver para carrinho aberto e troca de preço.
 */

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      /* Margem para esconder. Junto com `reveal_offset` forma a faixa morta que
         impede a barra de piscar quando o botão fica parado na borda da tela. */
      static HIDE_OFFSET = 50;

      connectedCallback() {
        this.revealOffset = parseInt(this.dataset.revealOffset, 10) || 0;
        this.scrollOffset = parseInt(this.dataset.scrollOffset, 10) || 0;
        this.targetMode = this.dataset.scrollTarget || 'auto';
        this.customTarget = this.dataset.customTarget || '';
        this.hideOnOverlay = this.dataset.hideOnOverlay === 'true';
        this.shouldHighlight = this.dataset.highlight === 'true';
        this.mirrorsPrice = this.dataset.mirrorPrice === 'true';
        this.reservesSpace = this.dataset.reserveSpace === 'true';

        this.isVisible = false;
        this.isPinnedByEditor = false;
        this.frame = null;
        /* Começa fora do alcance do teclado; `evaluate()` só chama `hide()`
           quando já estava visível, então o estado inicial vem daqui. */
        this.setAttribute('inert', '');

        this.button = this.querySelector('[data-satc-button]');
        if (!this.button) return;

        this.onScroll = this.onScroll.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onEditorSelect = this.onEditorSelect.bind(this);
        this.onEditorDeselect = this.onEditorDeselect.bind(this);

        this.button.addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);

        this.watchOverlays();
        this.watchPrice();
        this.reserveSpace();
        this.evaluate();
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        document.removeEventListener('shopify:section:select', this.onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        if (this.frame) cancelAnimationFrame(this.frame);
        [this.overlayObserver, this.priceObserver].forEach((o) => o && o.disconnect());
        document.documentElement.style.removeProperty('--satc-reserve');
      }

      /* ---------- Referências no DOM do produto ---------- */

      /* Escopo: a coluna de informações do produto principal. Sem isto,
         `button[name="add"]` pegaria o quick-add dos cards de produtos
         relacionados, que vem depois no documento. */
      get scope() {
        if (this._scope && this._scope.isConnected) return this._scope;
        this._scope =
          document.querySelector('product-info .product__info-container') ||
          document.querySelector('.product__info-container') ||
          document.querySelector('product-info');
        return this._scope;
      }

      /* O product-info.js troca este botão a cada re-render do Section
         Rendering API, então a referência é revalidada em vez de guardada. */
      get referenceButton() {
        if (this._button && this._button.isConnected) return this._button;
        const scope = this.scope;
        if (!scope) return null;
        this._button =
          scope.querySelector('.product-form__submit') || scope.querySelector('button[name="add"]');
        return this._button;
      }

      /* ---------- Mostrar / esconder ---------- */

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.evaluate();
        });
      }

      evaluate() {
        if (this.isPinnedByEditor) return;

        if (this.overlayIsOpen()) {
          this.hide();
          return;
        }

        const button = this.referenceButton;
        if (!button) return;

        const bottom = button.getBoundingClientRect().bottom;

        if (!this.isVisible && bottom < -this.revealOffset) {
          this.show();
        } else if (this.isVisible && bottom > StickyAddToCart.HIDE_OFFSET) {
          this.hide();
        }
      }

      /* `inert` faz o papel que seria de `visibility: hidden`: tira a barra do
         foco por teclado e do leitor de tela enquanto ela está fora da tela,
         sem participar de transição nenhuma. */
      show() {
        this.isVisible = true;
        this.classList.add('satc--visible');
        this.removeAttribute('inert');
      }

      hide() {
        this.isVisible = false;
        this.classList.remove('satc--visible');
        this.setAttribute('inert', '');
      }

      /* ---------- Carrinho / menu aberto ---------- */

      overlayIsOpen() {
        if (!this.hideOnOverlay) return false;
        /* O tema põe `overflow-hidden` no body em qualquer drawer ou modal;
           a classe `active` do cart-drawer cobre o caso do carrinho. */
        if (document.body.classList.contains('overflow-hidden')) return true;
        const drawer = document.querySelector('cart-drawer');
        return !!drawer && drawer.classList.contains('active');
      }

      watchOverlays() {
        if (!this.hideOnOverlay) return;
        this.overlayObserver = new MutationObserver(() => this.onScroll());
        this.overlayObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          this.overlayObserver.observe(drawer, {
            attributes: true,
            attributeFilter: ['class', 'open', 'aria-hidden'],
          });
        }
      }

      /* ---------- Espelho do preço ---------- */

      /* Em vez de reimplementar a lógica de variante, a barra copia o preço do
         botão real. Assim funciona tanto para a troca client-side do bundle
         quanto para o re-render do variant picker nativo. */
      watchPrice() {
        if (!this.mirrorsPrice) return;
        this.priceOut = this.querySelector('[data-satc-price]');
        this.compareOut = this.querySelector('[data-satc-compare]');
        if (!this.priceOut) return;

        const section = this.scope && this.scope.closest('.shopify-section');
        if (!section) return;

        this.priceObserver = new MutationObserver(() => {
          if (this.priceFrame) return;
          this.priceFrame = requestAnimationFrame(() => {
            this.priceFrame = null;
            this.syncPrice();
          });
        });
        this.priceObserver.observe(section, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });

        this.syncPrice();
      }

      syncPrice() {
        const button = this.referenceButton;
        if (!button || !this.priceOut) return;

        const price = button.querySelector('[data-submit-price]');
        const compare = button.querySelector('[data-submit-compare]');

        this.priceOut.textContent = price ? price.textContent.trim() : '';
        if (this.compareOut) {
          /* O botão real escreve o comparativo entre parênteses; aqui ele já
             vem riscado pelo CSS, então os parênteses saem. */
          this.compareOut.textContent = compare ? compare.textContent.trim().replace(/^\(|\)$/g, '') : '';
        }
        this.classList.toggle('satc--soldout', button.hasAttribute('disabled'));
      }

      /* ---------- Clique: rolar até as variantes ---------- */

      onClick(event) {
        event.preventDefault();

        const target = this.resolveTarget();
        if (!target) return;

        const top = Math.max(
          0,
          window.scrollY + target.getBoundingClientRect().top - this.headerOffset() - this.scrollOffset
        );

        window.scrollTo({ top, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });

        /* Leva o foco junto, senão quem navega por teclado continua no fim da
           página depois que a tela já voltou para o seletor. */
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });

        if (this.shouldHighlight) this.flash(target);
      }

      resolveTarget() {
        const scope = this.scope || document;

        if (this.targetMode === 'custom' && this.customTarget) {
          try {
            return document.querySelector(this.customTarget);
          } catch (error) {
            return null;
          }
        }

        const candidates = {
          bundle: ['.product__bundle', 'bundle-variantes'],
          variant_picker: ['variant-selects', '.product-form__input'],
          buy_buttons: ['.product-form__buttons'],
          auto: [
            '.product__bundle',
            'bundle-variantes',
            'variant-selects',
            '.product-form__input',
            '.product-form__buttons',
          ],
        }[this.targetMode] || [];

        for (const selector of candidates) {
          const found = scope.querySelector(selector);
          if (found) return found;
        }

        /* Última rede: o formulário de compra, que sempre existe. */
        return scope.querySelector('.product-form__buttons') || scope;
      }

      /* O cabeçalho fixo cobriria o topo do destino. Ele só é fixo quando o
         tema renderiza <sticky-header>; a altura vem de --header-height, que o
         próprio header.liquid mantém atualizada. */
      headerOffset() {
        if (!document.querySelector('sticky-header')) return 0;
        const declared = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height')
        );
        if (Number.isFinite(declared) && declared > 0) return declared;
        const header = document.querySelector('.section-header');
        return header ? header.offsetHeight : 0;
      }

      flash(target) {
        target.classList.remove('satc-target-flash');
        /* Reflow forçado: sem isto o browser reaproveita a animação anterior
           e o segundo clique seguido não pisca. */
        void target.offsetWidth;
        target.classList.add('satc-target-flash');
        target.addEventListener(
          'animationend',
          () => target.classList.remove('satc-target-flash'),
          { once: true }
        );
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /* ---------- Espaço no fim da página ---------- */

      /* A barra fica escondida com `visibility`, não `display: none`, então já
         tem altura medível no primeiro frame — dá para reservar o espaço antes
         de o cliente chegar ao rodapé, sem salto de layout. */
      reserveSpace() {
        if (!this.reservesSpace) return;
        const apply = () => {
          document.documentElement.style.setProperty('--satc-reserve', `${this.offsetHeight}px`);
        };
        apply();
        window.addEventListener('resize', apply, { passive: true });
      }

      /* ---------- Editor de temas ---------- */

      /* Sem isto, selecionar a seção no editor não mostra nada: a barra fica
         escondida até o cliente rolar, e o merchant não consegue ver o que edita. */
      onEditorSelect(event) {
        if (!event.target.contains(this)) return;
        this.isPinnedByEditor = true;
        this.show();
      }

      onEditorDeselect(event) {
        if (!event.target.contains(this)) return;
        this.isPinnedByEditor = false;
        this.evaluate();
      }
    }
  );
}
